import { baseApi } from "../../../../app/api";
import { store } from "../../../../app/store";
import { selectLanguageInfo } from "../../../../common/features/language/languageSelectors";
import { transformCart } from "../helpers/cartApiHelpers/transformCart";
import type { RawCart, RawCartCoupon } from "../../../../types";
import type {
  TransformedCart,
  UpdateCustomerPayload,
} from "../types/cartApiTypes";

const WC_STORE_API_BASE =
  window.commonVariables?.wcStoreApiBase || "/wp-json/wc/store/v1";

export const cartApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCart: builder.query<TransformedCart, void>({
      query: () => ({
        url: `${WC_STORE_API_BASE}/cart`,
      }),
      transformResponse: (response: RawCart) => {
        const languageInfo = selectLanguageInfo(store.getState());
        return transformCart(response, languageInfo);
      },
      providesTags: ["Cart"],
    }),

    addToCart: builder.mutation<
      TransformedCart,
      {
        id: number;
        quantity: number;
        variation?: { attribute: string; value: string }[];
      }
    >({
      query: (item) => ({
        url: `${WC_STORE_API_BASE}/cart/add-item`,
        method: "POST",
        body: item,
      }),
      transformResponse: (response: RawCart) => {
        const languageInfo = selectLanguageInfo(store.getState());
        return transformCart(response, languageInfo);
      },
      // Modifying cart items affects the Checkout draft order
      invalidatesTags: ["Cart", "Checkout"],
    }),

    updateCartItem: builder.mutation<
      TransformedCart,
      { key: string; quantity: number }
    >({
      query: (item) => ({
        url: `${WC_STORE_API_BASE}/cart/update-item`,
        method: "POST",
        body: item,
      }),
      transformResponse: (response: RawCart) => {
        const languageInfo = selectLanguageInfo(store.getState());
        return transformCart(response, languageInfo);
      },
      // Changing quantity changes totals
      invalidatesTags: ["Cart", "Checkout"],
    }),

    removeCartItem: builder.mutation<TransformedCart, { key: string }>({
      query: ({ key }) => ({
        url: `${WC_STORE_API_BASE}/cart/remove-item`,
        method: "POST",
        body: { key },
      }),
      transformResponse: (response: RawCart) => {
        const languageInfo = selectLanguageInfo(store.getState());
        return transformCart(response, languageInfo);
      },
      // Removing items changes totals
      invalidatesTags: ["Cart", "Checkout"],
    }),

    /**
     * Clears the entire cart using the standard WC Store API.
     * Used after successful off-site payments (like Iyzico).
     */
    clearCart: builder.mutation<void, void>({
      query: () => ({
        url: `${WC_STORE_API_BASE}/cart/items`, // Standard endpoint
        method: "DELETE", // Standard method for clearing
      }),
      // Force refresh of Cart and Checkout data
      invalidatesTags: ["Cart", "Checkout"],
    }),

    applyCoupon: builder.mutation<RawCartCoupon, { code: string }>({
      query: ({ code }) => ({
        url: `${WC_STORE_API_BASE}/cart/coupons`,
        method: "POST",
        body: { code },
      }),
      // Coupons affect totals
      invalidatesTags: ["Cart", "Checkout"],
    }),

    removeCoupon: builder.mutation<unknown, { code: string }>({
      query: ({ code }) => ({
        url: `${WC_STORE_API_BASE}/cart/coupons/${code}`,
        method: "DELETE",
      }),
      // Coupons affect totals
      invalidatesTags: ["Cart", "Checkout"],
    }),

    selectShippingRate: builder.mutation<
      TransformedCart,
      { package_id: number; rate_id: string }
    >({
      query: (rate) => ({
        url: `${WC_STORE_API_BASE}/cart/select-shipping-rate`,
        method: "POST",
        body: rate,
      }),
      transformResponse: (response: RawCart) => {
        const languageInfo = selectLanguageInfo(store.getState());
        return transformCart(response, languageInfo);
      },
      // Shipping costs affect totals
      invalidatesTags: ["Cart", "Checkout"],
    }),

    updateCustomer: builder.mutation<TransformedCart, UpdateCustomerPayload>({
      query: (payload) => ({
        url: `${WC_STORE_API_BASE}/cart/update-customer`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: RawCart) => {
        const languageInfo = selectLanguageInfo(store.getState());
        return transformCart(response, languageInfo);
      },
      // Address changes affect tax calculations and shipping options
      invalidatesTags: ["Cart", "Checkout"],
    }),
  }),
});

export const {
  useGetCartQuery,
  useAddToCartMutation,
  useUpdateCartItemMutation,
  useRemoveCartItemMutation,
  useClearCartMutation,
  useApplyCouponMutation,
  useRemoveCouponMutation,
  useSelectShippingRateMutation,
  useUpdateCustomerMutation,
} = cartApi;
